import { RefObject, useEffect, useState } from 'react'
import { useAppRootCompRef } from '../AppContext'

// Util methods to read/update steps scroll progress
export class StepScrollProgress {
  static setScrollProgressForAllSteps(
    scrollPercentage: number
  ): Record<number, number> {
    return { [-1]: scrollPercentage }
  }

  static setScrollProgressForStep(
    previousStepProgress: Record<number, number>,
    step: number,
    scrollPercentage: number
  ): Record<number, number> {
    return {
      ...previousStepProgress,
      [step]: scrollPercentage,
    }
  }

  static getScrollProgressForStep(
    stepsScrollProgress: Record<number, number>,
    step: number
  ) {
    return stepsScrollProgress[step] ?? stepsScrollProgress[-1] ?? 0
  }
}

export const isInScrollRange = (scrollPercentage: number) =>
  scrollPercentage > 0 && scrollPercentage < 1

// Some websites using scrollstories have a dedicated scroll container (like novartis).
// If this is the case, the container needs to be marked as data-scroll-container="scrollstory"
const getScrollContainer = (
  elementRef: RefObject<HTMLDivElement>
): Pick<Element, 'addEventListener' | 'removeEventListener'> => {
  const parentScrollContainer = elementRef.current?.closest(
    '[data-scroll-container="scrollstory"]'
  )
  if (parentScrollContainer) {
    return parentScrollContainer
  } else {
    return window
  }
}

// Calculates scroll percentage of given div in the viewport (number between 0 and 1)
export const useScrollPercentage = (
  elementRef: RefObject<HTMLDivElement>,
  shouldCalculate: boolean
): number => {
  const [scrollPercentage, setScrollPercentage] = useState(0)

  const calculateScrollProgress = (element: HTMLDivElement) => {
    const elementRect = element.getBoundingClientRect()

    // scroll distance until element appears in viewport
    const scrollPointOfAppearance = elementRect.top + window.scrollY
    // scroll distance until element disappears from viewport
    const scrollPointOfDisappearance =
      scrollPointOfAppearance + elementRect.height
    const currentScrollPoint = window.scrollY + window.innerHeight

    if (currentScrollPoint > scrollPointOfDisappearance) {
      return 1
    } else if (currentScrollPoint < scrollPointOfAppearance) {
      return 0
    } else {
      return (
        (currentScrollPoint - scrollPointOfAppearance) /
        (scrollPointOfDisappearance - scrollPointOfAppearance)
      )
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      if (elementRef.current) {
        const scrollProgress = calculateScrollProgress(elementRef.current)
        setScrollPercentage(scrollProgress)
      }
    }

    const scrollContainer = getScrollContainer(elementRef)

    if (shouldCalculate) {
      scrollContainer.addEventListener('scroll', handleScroll)
    } else {
      scrollContainer.removeEventListener('scroll', handleScroll)
    }

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
    }
  }, [elementRef, shouldCalculate])

  return scrollPercentage
}

export function useGlobalScrollPercentage(): number | null {
  const [scrollPercentage, setScrollPercentage] = useState<number | null>(null)
  const appRootCompRef = useAppRootCompRef()

  useEffect(() => {
    const handleScroll = () => {
      if (!appRootCompRef?.current) {
        return
      }

      const rootElement = appRootCompRef.current
      const rect = rootElement.getBoundingClientRect()

      if (window.scrollY < 1) {
        setScrollPercentage(0)
        return
      }

      // Calculate when the bottom of the root element enters the viewport
      const scrollPointOfAppearance = rect.top + window.scrollY + window.innerHeight

      // Calculate when the bottom of the root element exits the viewport
      const scrollPointOfDisappearance = rect.bottom + window.scrollY

      // Current scroll position (bottom of viewport)
      const currentScrollPoint = window.scrollY + window.innerHeight

      let scrollProgress = 0
      if (currentScrollPoint > scrollPointOfDisappearance) {
        scrollProgress = 1
      } else if (currentScrollPoint < scrollPointOfAppearance) {
        scrollProgress = 0
      } else {
        scrollProgress = (currentScrollPoint - scrollPointOfAppearance) /
          (scrollPointOfDisappearance - scrollPointOfAppearance)
      }

      setScrollPercentage(scrollProgress)
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [appRootCompRef])

  return scrollPercentage
}
